import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Icon — ported from TB Design System (`@tb/icon`).
 *
 * The 13 brand glyphs: 1.5px stroke, 24 viewBox, round caps and joins, colour
 * through `currentColor`. Anything outside the set is Lucide (steering:
 * media-optimization).
 */
const paths: Record<string, React.ReactNode> = {
  account: (
    <>
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5 20c1.4-3.4 4-5 7-5s5.6 1.6 7 5" />
    </>
  ),
  wishlist: (
    <path d="M12 20s-7-4.2-7-10.5A4.5 4.5 0 0 1 9.5 5c1.5 0 2.6.8 3.5 2 .9-1.2 2-2 3.5-2A4.5 4.5 0 0 1 21 9.5C21 15.8 14 20 14 20" />
  ),
  bag: (
    <>
      <path d="M5 8h14l-1 12H6L5 8z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-3.8-3.8" />
    </>
  ),
  menu: (
    <>
      <path d="M4 8h16" />
      <path d="M4 16h16" />
    </>
  ),
  "chevron-down": <path d="m6 9 6 6 6-6" />,
  "arrow-left": (
    <>
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </>
  ),
  "arrow-right": (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M7 7v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7" />
      <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  "shield-check": (
    <>
      <path d="M21 10V8a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h6" />
      <path d="m17 17 2 2 4-4" />
    </>
  ),
};

export type IconName = keyof typeof paths;

export const iconNames = Object.keys(paths) as IconName[];

export interface IconProps extends Omit<React.SVGAttributes<SVGSVGElement>, "name"> {
  name: IconName;
  /** Size in px (width = height). Default 24. */
  size?: number;
  /** Accessible label. If omitted, icon is `aria-hidden`. */
  label?: string;
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ name, size = 24, label, className, ...props }, ref) => (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("inline-block shrink-0", className)}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...props}
    >
      {paths[name]}
    </svg>
  ),
);
Icon.displayName = "Icon";
